import type { TFunction } from 'i18next'
import type { RelatedLink } from '@/components/marketing/related-links'
import { MARKETING_PAGES, type MarketingPageKey } from '@/components/marketing/links'
import { BLOG_POSTS, findBlogPost } from './posts'

const isMarketingKey = (key: string): key is MarketingPageKey =>
  MARKETING_PAGES.some((page) => page.key === key)

/**
 * Build related-link cards for a blog post. Each reference is either a marketing
 * page key (copy from `marketing.<key>.link.*`, path from the catalogue) or
 * another post's slug (copy from `blog.<i18nKey>.link.*`). Self-references and
 * unresolved keys are dropped so a typo degrades to a missing card, never a
 * crash under the prerender.
 */
export function buildBlogRelatedLinks(
  t: TFunction,
  currentSlug: string,
  keys: readonly string[],
): readonly RelatedLink[] {
  return keys
    .filter((key) => key !== currentSlug)
    .map((key): RelatedLink | null => {
      if (isMarketingKey(key)) {
        const page = MARKETING_PAGES.find((p) => p.key === key)!
        return {
          to: page.to,
          title: t(`marketing.${key}.link.title`),
          description: t(`marketing.${key}.link.description`),
        }
      }
      const post = findBlogPost(key)
      if (!post) return null
      return {
        to: `/blog/${post.slug}`,
        title: t(`blog.${post.i18nKey}.link.title`),
        description: t(`blog.${post.i18nKey}.link.description`),
      }
    })
    .filter((link): link is RelatedLink => link !== null)
}

export { BLOG_POSTS }
